/**
 * Typed client for /embed/v1/*.
 *
 * Both popup and redirect modes use this client. The auth contract
 * differs only in HOW the EmbedClient public_key is sourced:
 *   - Popup: passed via querystring on the iframe src (data-key on
 *     the trigger element → loader.js → iframe URL).
 *   - Redirect: encoded in the URL itself (?via=PUB_KEY) or attached
 *     to a per-org slug route (resolved server-side at first byte).
 *
 * The embed-scoped JWT is held in sessionStorage for the duration of
 * the registration session — partitioned per-origin in popup mode
 * (browser does this for sandboxed cross-origin iframes), normal
 * sessionStorage in redirect mode.
 */

import axios, { AxiosInstance } from 'axios';

const ACCESS_TOKEN_KEY = 'tn-embed-access-token';
const REFRESH_TOKEN_KEY = 'tn-embed-refresh-token';

/** An organizer's registration question (backend services/registration_questions.py). */
export interface RegistrationQuestion {
  id: string;
  type: 'text' | 'number' | 'choice';
  label: string;
  help: string | null;
  required: boolean;
  options: string[];
  min: number | null;
  max: number | null;
}

export interface BrandingResponse {
  /** Null = show no organizer attribution (GFG individual-account orgs,
   *  whose auto-generated org name is a person's name or email). */
  organizer_name: string | null;
  /** Usually /org-media/… on the widget's own CDN edge. */
  organizer_logo_url: string | null;
  /** The stored URL, if the edge copy fails to load. */
  organizer_logo_fallback_url?: string | null;
}

export interface AllowedEvent {
  id: string;
  name: string;
  /** 'club' | 'charity' — drives entrant-vs-sponsor copy. */
  event_type: string;
  /** Event has divisions → the entrant registration flow is available. */
  has_registration: boolean;
  /** Event has ≥1 publicly-purchasable sponsorship tier. */
  has_sponsorships: boolean;
}

export interface SponsorshipFulfillmentField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  hint?: string | null;
}

export interface SponsorshipTier {
  id: string;
  tier_name: string;
  tier_slug: string | null;
  price_cents: number;
  description: string | null;
  whats_included: string[];
  quantity_remaining: number;
  requires_approval: boolean;
  fulfillment_fields: SponsorshipFulfillmentField[];
  /** Digital Contact Card addon: whether this placement tier can buy it. */
  addon_available?: boolean;
  /** The addon's flat platform price when available (cents). */
  addon_price_cents?: number;
}

export interface SponsorCheckoutBody {
  sponsor_name: string;
  sponsor_email: string;
  sponsor_contact_name?: string;
  sponsor_phone?: string;
  /** Card content (coupon / contact_card) rides as objects; plain declared
   *  fields as strings — the server only accepts what the tier declares
   *  plus the paid addon keys. */
  fulfillment_data?: Record<string, unknown>;
  /** Digital Contact Card addon (+$10, hole/scorecard placements). */
  offer_addon?: boolean;
}

export interface SponsorshipOrderSummary {
  tier_name: string;
  price_cents: number;
  /** Digital Contact Card addon charged (0 when not purchased). */
  offer_addon_cents?: number;
  /** Buyer-side platform fee added at checkout (server-quoted). */
  platform_fee_cents?: number;
  processing_fee_cents: number;
  total_cents: number;
  requires_approval: boolean;
}

export interface SponsorshipCheckoutResponse {
  stripe_client_secret: string;
  stripe_payment_intent_id: string;
  order_summary: SponsorshipOrderSummary;
}

export interface SponsorshipConfirmResponse {
  status: 'purchased' | 'pending_approval';
  assignment_id: string;
  confirmation_number: string;
  message: string;
  fulfillment_complete: boolean;
  fulfillment_url?: string | null;
}

export interface ConfigResponse {
  embed_client_id: string;
  branding: BrandingResponse;
  allowed_events: AllowedEvent[];
  return_url: string | null;
  /** First-party skin preset: 'tn' (default) | 'gfg'. Token CSS arrives
   *  render-blocking via /themes/by-key/{key}.css; this name only drives
   *  component-level variants (the GFG brand header in Chrome). */
  theme?: 'tn' | 'gfg' | string;
  /** Kill switches for newer widget features (see src/flags.ts). A feature
   *  is on unless this says `false`. */
  features?: Record<string, boolean>;
}

export interface DivisionResponse {
  id: number;
  name: string;
  capacity: number | null;
  display_order: number;
  /** Capacity reached — render greyed out, not selectable. */
  is_full: boolean;
  /** Registration window (division or event level) not currently open. */
  is_closed: boolean;
}

/** A team already registered for the event ("Who's playing"). */
export interface RegisteredTeam {
  id: string;
  name: string;
  division: string | null;
  city: string | null;
  state: string | null;
  logo_url: string | null;
  coach: string | null;
  status: string;
}

export interface RegisteredTeamsResponse {
  teams: RegisteredTeam[];
  total: number;
  /** The organizer hid the team list: teams is empty, so "Who's playing" doesn't show. */
  hidden?: boolean;
  division_counts?: Record<string, number>;
}

/** One applied discount in a price quote. */
export interface QuoteDiscount {
  /** 'BULK' | 'COUPON' | 'LOYALTY' — drives the label shown to the coach. */
  type: string;
  name: string | null;
  discount_cents: number;
}

/** Read-only price preview for the confirm screen. */
export interface QuoteResponse {
  fee_cents: number;
  discount_cents: number;
  credit_cents: number;
  net_cents: number;
  discounts: QuoteDiscount[];
  /** What a card payment adds on top of net_cents, and its total (the same
   *  charge the payment step makes; add-ons not included). */
  platform_fee_cents?: number;
  processing_fee_cents?: number;
  card_total_cents?: number;
}

export interface CoachProfile {
  id: number;
  email: string;
  role_preference: string | null;
  /**
   * 'stub' = embed-only User, no Cognito account.
   * 'real' = Cognito-backed account.
   *
   * The Confirm page reads this to decide whether to show the
   * "Create a TourneyNation account" checkbox.
   */
  account_status: 'stub' | 'real';
  /** False for a coach who signed in with a new email: we don't know
   *  their name yet, so the new-team form asks for it. */
  has_name?: boolean;
}

export interface GuestCreateTeamRequest {
  /** Team name. Required in both guest and authenticated modes. */
  name: string;
  /** Required when no auth token is present (guest path). */
  coach_name?: string;
  coach_email?: string;
  /** Event public_id (UUID). Drives sport/age-group derivation server-side. */
  event_id?: string;
  division?: string;
}

export interface GuestCreateTeamResponse {
  team: TeamSummary;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  account_status: 'stub' | 'real';
}

export interface PromoteToAccountResponse {
  account_status: 'real';
  message: string;
}

export interface TeamSummary {
  id: string;
  name: string;
  sport: string | null;
  age_group: string | null;
  gender: string | null;
  city: string | null;
  state: string | null;
  roster_count: number;
}

export interface AvailableEvent {
  id: string;
  name: string;
  event_date: string | null;
  /** Last day of a multi-day tournament. */
  ends_at?: string | null;
  venue_name: string | null;
  location: string | null;
  status: string;
  registration_mode: 'open' | 'approval_required';
  fee_cents: number;
  /** Lowest / highest price across the event's divisions (equal when the
   *  price doesn't vary). Missing from older APIs. */
  fee_min_cents?: number;
  fee_max_cents?: number;
  /** Organizer accepts out-of-band payment (Venmo/Cash App/cash/check). When
   *  true the Confirm screen offers a "Pay offline" choice next to card. */
  offline_payment_enabled?: boolean;
  offline_venmo?: string | null;
  offline_cashapp?: string | null;
  /** The organizer lets teams register now and pay by card later: Confirm
   *  offers "Pay later" next to card. */
  pay_later_enabled?: boolean;
}

export interface EligibilityResponse {
  eligible: boolean;
  reason: string | null;
}

export interface TeamRegistration {
  id: string;
  team_id: string;
  event_id: string;
  status: string;
  division: string | null;
  amount_cents: number | null;
  created_at: string;
  /** Present only when the entrant chose offline payment — the instruction
   *  screen reads these to show where to send the money and how much. */
  payment_method?: string | null;
  offline_memo_code?: string | null;
  offline_venmo?: string | null;
  offline_cashapp?: string | null;
  offline_amount_cents?: number | null;
}

/** One public link per team registration: a parent opens it without an
 *  account, adds their player and signs the tournament's player waivers. */
export interface ParentLink {
  url: string;
  token: string;
  enabled: boolean;
  open: boolean;
  /** 'off' | 'locked' | 'not_registered' when closed. */
  closed_reason: string | null;
  closes_at: string | null;
}

// ── Bulk registration (one team, several events, one payment) ──────────

export interface BatchEventItem {
  event_id: string;
  division?: string;
  /** Answers to this event's registration questions, keyed by question id. */
  answers?: Record<string, string>;
}

export interface BatchPreviewItem {
  event_id: string;
  event_name: string;
  fee_cents: number;
  /** The larger of the event's own discount and the multi-event discount. */
  discount_cents: number;
  credit_cents: number;
  platform_fee_cents?: number;
  processing_fee_cents?: number;
  /** False for a tournament that needs approval: charged once approved. */
  pays_now?: boolean;
}

/** One of the organizer's multi-event discount tiers (BulkBasis.EVENTS). */
export interface BulkOffer {
  min_events: number;
  /** 'PERCENT' | 'FLAT' */
  discount_kind: string;
  percent_off: number | null;
  amount_off_cents: number | null;
  name: string | null;
}

export interface BatchPreview {
  count: number;
  subtotal_cents: number;
  discount_cents: number;
  credit_cents: number;
  total_cents: number;
  platform_fee_cents?: number;
  processing_fee_cents?: number;
  /** What the card is charged today: entry fees of the tournaments that don't
   *  need approval, plus platform and processing (as the payment step). */
  card_total_cents?: number;
  items: BatchPreviewItem[];
}

export interface BatchRegistration {
  id: string;
  event_id: string | null;
  event_name: string | null;
  event_date: string | null;
  division: string | null;
  /** pending_payment | pending_approval | confirmed | … */
  status: string;
  amount_cents: number;
  discount_cents: number;
  processing_fee_cents: number;
}

export interface BatchRegisterResponse {
  batch_id: string | null;
  registrations: BatchRegistration[];
  /** Events the server didn't register: { event_id, reason }. */
  skipped: Array<{ event_id: string; reason: string }>;
  total_discount_cents: number;
  total_credit_cents: number;
}

export interface BatchPaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  amount_cents: number;
  fee_cents: number;
  /** Platform fee the team pays across the batch (0 when the organizer covers it). */
  platform_fee_cents?: number;
  processing_fee_cents: number;
  registrations: BatchRegistration[];
}

export interface BatchConfirmResponse {
  batch_id: string;
  registrations: BatchRegistration[];
}

export interface CreatePaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  /** Grand total actually charged. */
  amount_cents: number;
  /** Itemization for the payment screen. */
  fee_cents: number;
  addon_total_cents: number;
  processing_fee_cents: number;
  /** Platform fee the team pays (0 when the organizer covers it). */
  platform_fee_cents?: number;
}

export interface AddonProduct {
  id: string;
  name: string;
  description: string | null;
  price_cents: number;
  kind: string;
  max_per_order: number | null;
  quantity_remaining: number | null;
}

export interface AddonItem {
  product_id: string;
  quantity: number;
  variant_id?: string;
}

export class EmbedClient {
  private http: AxiosInstance;
  private baseUrl: string;
  public readonly publicKey: string;
  /** Single-flight guard: parallel 401s share one refresh call. */
  private refreshInFlight: Promise<boolean> | null = null;
  /** Public event reads (divisions, registered teams) by URL, kept for a
   *  short while so a prefetch from an earlier screen is the request the
   *  division screen uses. A failed read is dropped so the next try refetches. */
  private publicReads = new Map<string, { at: number; promise: Promise<unknown> }>();
  private me: { token: string | null; promise: Promise<CoachProfile> } | null = null;
  private previews = new Map<string, { at: number; promise: Promise<BatchPreview> }>();

  constructor(opts: { publicKey: string; baseUrl?: string }) {
    this.publicKey = opts.publicKey;
    this.baseUrl = opts.baseUrl ?? '';
    this.http = axios.create({
      baseURL: this.baseUrl,
      withCredentials: false,
      headers: { 'Content-Type': 'application/json' },
    });

    this.http.interceptors.request.use((config) => {
      const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });

    // The access token only lives 15 minutes — shorter than plenty of real
    // registration sessions (create a team, review, find the credit card).
    // On a 401, swap the refresh token for fresh JWTs and retry the request
    // once. Critically this covers the post-payment /confirm call, which
    // otherwise fails AFTER Stripe has already charged the card.
    this.http.interceptors.response.use(
      (resp) => resp,
      async (error) => {
        const original = error?.config;
        if (
          error?.response?.status !== 401
          || !original
          || (original as any)._tnRetried
        ) {
          return Promise.reject(error);
        }
        const refreshed = await this.tryRefreshSession();
        if (!refreshed) return Promise.reject(error);
        (original as any)._tnRetried = true;
        // The request interceptor above re-reads sessionStorage, so the
        // retry picks up the new access token automatically.
        return this.http(original);
      },
    );
  }

  private tryRefreshSession(): Promise<boolean> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.doRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;
    try {
      // Bare axios, not this.http — the interceptor must never recurse
      // into itself if the refresh call is the one that 401s.
      const { data } = await axios.post<{
        access_token: string;
        refresh_token: string;
        expires_in: number;
      }>(`${this.baseUrl}/embed/v1/auth/refresh`, {
        refresh_token: refreshToken,
        embed_client_key: this.publicKey,
      });
      sessionStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      return true;
    } catch {
      return false;
    }
  }

  // ── public ──────────────────────────────────────────────────────────

  async getConfig(): Promise<ConfigResponse> {
    const { data } = await this.http.get<ConfigResponse>('/embed/v1/config', {
      params: { key: this.publicKey },
    });
    return data;
  }

  /** The organizer's registration questions, asked on Confirm before payment. */
  async getRegistrationQuestions(eventId: string): Promise<RegistrationQuestion[]> {
    const { data } = await this.http.get<{ questions: RegistrationQuestion[] }>(
      `/embed/v1/events/${eventId}/registration-questions`,
      { params: { key: this.publicKey } },
    );
    return data.questions ?? [];
  }

  getDivisions(eventId: string): Promise<DivisionResponse[]> {
    return this.publicRead(`/embed/v1/events/${eventId}/divisions`);
  }

  /** Teams already registered for the event (public), for each division's
   *  "See teams". */
  getRegisteredTeams(eventId: string): Promise<RegisteredTeamsResponse> {
    return this.publicRead(`/embed/v1/events/${eventId}/registered-teams`);
  }

  /** Start loading an event's division screen before it's shown (the event
   *  row is touched, or the Register / Sponsor screen opens). Fire and
   *  forget: the division screen picks up the same requests. */
  prefetchEvent(eventId: string): void {
    this.getDivisions(eventId).catch(() => {});
    this.getRegisteredTeams(eventId).catch(() => {});
  }

  private publicRead<T>(url: string, maxAgeMs = 30_000): Promise<T> {
    const hit = this.publicReads.get(url);
    if (hit && Date.now() - hit.at < maxAgeMs) return hit.promise as Promise<T>;
    const promise = this.http
      .get<T>(url, { params: { key: this.publicKey } })
      .then(({ data }) => data);
    this.publicReads.set(url, { at: Date.now(), promise });
    promise.catch(() => {
      if (this.publicReads.get(url)?.promise === promise) this.publicReads.delete(url);
    });
    return promise;
  }

  /**
   * Price preview for the confirm screen — division-resolved fee plus any
   * automatic discount/credit. Matches what registerTeam will charge, so the
   * summary shows the real price (not the event default) and can explain the
   * discount. Requires the embed coach JWT (same as eligibility/register).
   */
  async getQuote(
    teamId: string,
    eventId: string,
    division?: string,
  ): Promise<QuoteResponse> {
    const { data } = await this.http.get<QuoteResponse>(
      `/embed/v1/teams/${teamId}/quote`,
      { params: { key: this.publicKey, event_id: eventId, division } },
    );
    return data;
  }

  // ── sponsorships (public — no auth) ────────────────────────────────

  async getSponsorships(eventId: string): Promise<SponsorshipTier[]> {
    const { data } = await this.http.get<SponsorshipTier[]>(
      `/embed/v1/events/${eventId}/sponsorships`,
      { params: { key: this.publicKey } },
    );
    return data;
  }

  /** Store and clean a logo picked on checkout (data URI). Returns the
   *  stored logo_url for fulfillment_data and, when it could be cleaned, the
   *  cleaned copy to preview. */
  async uploadSponsorLogo(
    eventId: string,
    dataUri: string,
  ): Promise<{ logo_url: string; clean: { url: string } | null }> {
    const { data } = await this.http.post<{ logo_url: string; clean: { url: string } | null }>(
      `/embed/v1/events/${eventId}/sponsor-logo`,
      { data_uri: dataUri },
      { params: { key: this.publicKey } },
    );
    return data;
  }

  async checkoutSponsorship(
    eventId: string,
    sponsorshipId: string,
    body: SponsorCheckoutBody,
  ): Promise<SponsorshipCheckoutResponse> {
    const { data } = await this.http.post<SponsorshipCheckoutResponse>(
      `/embed/v1/events/${eventId}/sponsorships/${sponsorshipId}/checkout`,
      { embed_client_key: this.publicKey, ...body },
    );
    return data;
  }

  async confirmSponsorship(
    eventId: string,
    sponsorshipId: string,
    body: SponsorCheckoutBody & { stripe_payment_intent_id: string },
  ): Promise<SponsorshipConfirmResponse> {
    const { data } = await this.http.post<SponsorshipConfirmResponse>(
      `/embed/v1/events/${eventId}/sponsorships/${sponsorshipId}/confirm`,
      { embed_client_key: this.publicKey, ...body },
    );
    return data;
  }

  /**
   * Email-first sign-in: sends a code only when the email has an account or
   * a saved team. Resolves false for a new email (carry on as a guest). An
   * API without the lookup always sends, which reads as true.
   */
  async requestCode(email: string): Promise<boolean> {
    const { data } = await this.http.post<{ code_sent?: boolean }>('/embed/v1/auth/request-code', {
      email,
      embed_client_key: this.publicKey,
      only_if_known: true,
    });
    return data?.code_sent !== false;
  }

  async verifyCode(email: string, code: string): Promise<void> {
    const { data } = await this.http.post<{
      access_token: string;
      refresh_token: string;
      expires_in: number;
    }>('/embed/v1/auth/verify-code', {
      email,
      code,
      embed_client_key: this.publicKey,
    });
    sessionStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  }

  signOut(): void {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  isAuthed(): boolean {
    return Boolean(sessionStorage.getItem(ACCESS_TOKEN_KEY));
  }

  /**
   * Create a team inline from the embed flow. Powers both:
   *   - guest checkout (no JWT yet) — provide coach_name, coach_email,
   *     event_id, division. Returns access + refresh tokens to use
   *     for the rest of the flow.
   *   - authenticated coach using the "+ Create new team" tile on
   *     the team picker. JWT is sent automatically; only `name` is
   *     required in the payload. Server rotates the tokens; the
   *     client should adopt the new ones.
   */
  async createTeam(req: GuestCreateTeamRequest): Promise<GuestCreateTeamResponse> {
    const { data } = await this.http.post<GuestCreateTeamResponse>(
      '/embed/v1/teams',
      { embed_client_key: this.publicKey, ...req },
    );
    // Adopt fresh tokens for either mode — keeps the rest of the flow
    // consistent regardless of how we arrived at this team.
    sessionStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
    return data;
  }

  /**
   * Promote an embed-only stub user to a real Cognito-backed account.
   * Called from the DonePage when the "Create account" checkbox was
   * checked on the Confirm page — *after* successful payment so a
   * Cognito error never breaks a paid registration.
   *
   * Returns 200 with the new status on success, 409 on email
   * collision with an existing Cognito user, 503 on misconfiguration.
   */
  async promoteToAccount(): Promise<PromoteToAccountResponse> {
    this.me = null; // account_status changes
    const { data } = await this.http.post<PromoteToAccountResponse>(
      '/embed/v1/me/promote-to-account',
      {},
    );
    return data;
  }

  // ── authenticated ───────────────────────────────────────────────────

  /** The signed-in coach. Kept per sign-in token, so Confirm and Done share
   *  one request instead of Done waiting on its own; a new token (sign-in,
   *  new team as a guest) or creating the account reads it fresh. */
  getMe(): Promise<CoachProfile> {
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (this.me && this.me.token === token) return this.me.promise;
    const promise = this.http.get<CoachProfile>('/embed/v1/me').then(({ data }) => data);
    this.me = { token, promise };
    promise.catch(() => {
      if (this.me?.promise === promise) this.me = null;
    });
    return promise;
  }

  async getTeams(): Promise<TeamSummary[]> {
    const { data } = await this.http.get<TeamSummary[]>('/embed/v1/teams');
    return data;
  }

  async getAvailableEvents(teamId: string): Promise<AvailableEvent[]> {
    const { data } = await this.http.get<AvailableEvent[]>(
      `/embed/v1/teams/${teamId}/available-events`,
    );
    return data;
  }

  async checkEligibility(
    teamId: string,
    eventId: string,
    division?: string,
  ): Promise<EligibilityResponse> {
    const { data } = await this.http.get<EligibilityResponse>(
      `/embed/v1/teams/${teamId}/eligible`,
      { params: { event_id: eventId, division } },
    );
    return data;
  }

  async registerTeam(
    teamId: string,
    eventId: string,
    division?: string,
    opts?: { paymentMethod?: 'card' | 'offline' | 'pay_later'; addonItems?: AddonItem[]; answers?: Record<string, string> },
  ): Promise<TeamRegistration> {
    // Default (card) path sends only event/division and heads to Stripe next.
    // The offline path carries payment_method + any add-ons here, since there
    // is no create-payment-intent call to fold them into.
    const body: Record<string, unknown> = { event_id: eventId, division };
    if (opts?.paymentMethod) body.payment_method = opts.paymentMethod;
    if (opts?.addonItems && opts.addonItems.length) body.addon_items = opts.addonItems;
    if (opts?.answers && Object.keys(opts.answers).length) body.answers = opts.answers;
    const { data } = await this.http.post<TeamRegistration>(
      `/embed/v1/teams/${teamId}/register`,
      body,
    );
    return data;
  }

  /** The registration's parent link (/j/<token> on TourneyNation). */
  async getParentLink(teamId: string, registrationId: string): Promise<ParentLink> {
    const { data } = await this.http.get<ParentLink>(
      `/embed/v1/teams/${teamId}/registrations/${registrationId}/parent-link`,
    );
    return data;
  }

  // ── Bulk registration ────────────────────────────────────────────────

  /** The organizer's multi-event tiers, lowest threshold first. */
  async getBulkOffers(): Promise<BulkOffer[]> {
    const { data } = await this.http.get<BulkOffer[]>('/embed/v1/bulk-offers');
    // Optional garnish: anything but a list means no offers, never a crash.
    return Array.isArray(data) ? data : [];
  }

  /** Price a bulk selection. Kept 30s by team + events + divisions, so the
   *  divisions step can price the picks in the background and the review
   *  step opens on that same request. Answers don't change prices. */
  previewBatch(teamId: string, events: BatchEventItem[]): Promise<BatchPreview> {
    const items = events.map((e) => ({ event_id: e.event_id, division: e.division ?? null }));
    const key = `${teamId}|${JSON.stringify(items.map((i) => [i.event_id, i.division]))}`;
    const hit = this.previews.get(key);
    if (hit && Date.now() - hit.at < 30_000) return hit.promise;
    const promise = this.http
      .post<BatchPreview>(`/embed/v1/teams/${teamId}/register-batch/preview`, { events: items })
      .then(({ data }) => data);
    this.previews.set(key, { at: Date.now(), promise });
    promise.catch(() => {
      if (this.previews.get(key)?.promise === promise) this.previews.delete(key);
    });
    return promise;
  }

  async registerBatch(
    teamId: string,
    events: BatchEventItem[],
    opts?: { paymentMethod?: 'card' | 'pay_later' },
  ): Promise<BatchRegisterResponse> {
    const { data } = await this.http.post<BatchRegisterResponse>(
      `/embed/v1/teams/${teamId}/register-batch`,
      { events, ...(opts?.paymentMethod ? { payment_method: opts.paymentMethod } : {}) },
    );
    this.previews.clear(); // prices may move once these exist (credit used, etc.)
    return data;
  }

  async createBatchPaymentIntent(batchId: string): Promise<BatchPaymentIntentResponse> {
    const { data } = await this.http.post<BatchPaymentIntentResponse>(
      `/embed/v1/batches/${batchId}/create-payment-intent`,
    );
    return data;
  }

  async confirmBatch(batchId: string): Promise<BatchConfirmResponse> {
    const { data } = await this.http.post<BatchConfirmResponse>(
      `/embed/v1/batches/${batchId}/confirm`,
    );
    return data;
  }

  async createPaymentIntent(
    teamId: string,
    registrationId: string,
    addonItems?: AddonItem[],
  ): Promise<CreatePaymentIntentResponse> {
    // Body is optional — a fee-only registration sends none. When charity
    // add-ons are selected they're folded into this single PaymentIntent.
    const body = addonItems && addonItems.length ? { addon_items: addonItems } : undefined;
    const { data } = await this.http.post<CreatePaymentIntentResponse>(
      `/embed/v1/teams/${teamId}/registrations/${registrationId}/create-payment-intent`,
      body,
    );
    return data;
  }

  async getAddons(eventId: string): Promise<AddonProduct[]> {
    const { data } = await this.http.get<AddonProduct[]>(
      `/embed/v1/events/${eventId}/addons`,
      { params: { key: this.publicKey } },
    );
    return data;
  }

  async confirmRegistration(
    teamId: string,
    registrationId: string,
  ): Promise<{ registration: TeamRegistration }> {
    const { data } = await this.http.post<{ registration: TeamRegistration }>(
      `/embed/v1/teams/${teamId}/registrations/${registrationId}/confirm`,
    );
    return data;
  }
}

// ── Public sponsor fulfillment (no embed key) ───────────────────────────
//
// The post-purchase asset-upload page is reached from an emailed link that
// carries only the unguessable fulfillment_token — there's no embed public
// key or JWT in play. These are plain same-origin calls to /embed/v1/*.

export interface SponsorFulfillmentData {
  sponsor_name: string;
  event_name: string;
  tier_name: string;
  fulfillment_fields: SponsorshipFulfillmentField[];
  fulfillment_data: Record<string, string>;
  fulfillment_status: string;
}

export async function getSponsorFulfillment(token: string): Promise<SponsorFulfillmentData> {
  const { data } = await axios.get<SponsorFulfillmentData>(
    `/embed/v1/sponsor-fulfillment/${encodeURIComponent(token)}`,
  );
  return data;
}

export async function submitSponsorFulfillment(
  token: string,
  fulfillmentData: Record<string, string>,
): Promise<SponsorFulfillmentData> {
  const { data } = await axios.put<SponsorFulfillmentData>(
    `/embed/v1/sponsor-fulfillment/${encodeURIComponent(token)}`,
    { fulfillment_data: fulfillmentData },
  );
  return data;
}

// ── Public sponsor invite accept (no embed key) ─────────────────────────
//
// An organizer-invited sponsor lands here from the "Accept sponsorship
// invite" email. The invite_token is the only credential; the assignment
// (and its reserved slot) already exists — accepting just settles payment.

export interface SponsorInviteData {
  sponsor_name: string;
  event_name: string;
  tier_name: string;
  price_cents: number;
  platform_fee_cents?: number;
  processing_fee_cents: number;
  total_cents: number;
  status: string;
  payment_status: string;
  already_accepted: boolean;
  expired: boolean;
  expires_at: string | null;
}

export interface SponsorInviteCheckout {
  is_free: boolean;
  stripe_client_secret: string | null;
  stripe_payment_intent_id: string | null;
  order_summary: {
    tier_name: string;
    price_cents: number;
    platform_fee_cents?: number;
    processing_fee_cents: number;
    total_cents: number;
  };
}

export interface SponsorInviteConfirmResult {
  status: string;
  confirmation_number: string;
  message: string;
  fulfillment_complete: boolean;
  fulfillment_url: string | null;
}

export async function getSponsorInvite(token: string): Promise<SponsorInviteData> {
  const { data } = await axios.get<SponsorInviteData>(
    `/embed/v1/sponsor-invite/${encodeURIComponent(token)}`,
  );
  return data;
}

export async function checkoutSponsorInvite(token: string): Promise<SponsorInviteCheckout> {
  const { data } = await axios.post<SponsorInviteCheckout>(
    `/embed/v1/sponsor-invite/${encodeURIComponent(token)}/checkout`,
  );
  return data;
}

export async function confirmSponsorInvite(
  token: string,
  stripePaymentIntentId?: string | null,
): Promise<SponsorInviteConfirmResult> {
  const { data } = await axios.post<SponsorInviteConfirmResult>(
    `/embed/v1/sponsor-invite/${encodeURIComponent(token)}/confirm`,
    { stripe_payment_intent_id: stripePaymentIntentId ?? null },
  );
  return data;
}

// ── Digital Contact Card for an offline sponsor (no embed key) ──────────
//
// The organizer emails an offline sponsor a /sponsor-contact-card/{token}
// link; the sponsor writes an offer or contact card and pays for it.

export interface SponsorContactCardData {
  sponsor_name: string;
  event_name: string;
  tier_name: string;
  hole_number: number | null;
  logo_url: string | null;
  price_cents: number;
  processing_fee_cents: number;
  total_cents: number;
  already_paid: boolean;
}

export interface SponsorContactCardCheckout {
  stripe_client_secret: string;
  stripe_payment_intent_id: string;
  order_summary: SponsorContactCardData;
}

export interface SponsorContactCardContent {
  /** expiry: optional "Good through" date, YYYY-MM-DD. */
  coupon?: { headline: string; code?: string; expiry?: string };
  contact_card?: { memo?: string; phone?: string; email?: string };
}

export async function getSponsorContactCard(token: string): Promise<SponsorContactCardData> {
  const { data } = await axios.get<SponsorContactCardData>(
    `/embed/v1/sponsor-contact-card/${encodeURIComponent(token)}`,
  );
  return data;
}

export async function checkoutSponsorContactCard(token: string): Promise<SponsorContactCardCheckout> {
  const { data } = await axios.post<SponsorContactCardCheckout>(
    `/embed/v1/sponsor-contact-card/${encodeURIComponent(token)}/checkout`,
  );
  return data;
}

export async function confirmSponsorContactCard(
  token: string,
  stripePaymentIntentId: string,
  content: SponsorContactCardContent,
): Promise<void> {
  await axios.post(`/embed/v1/sponsor-contact-card/${encodeURIComponent(token)}/confirm`, {
    stripe_payment_intent_id: stripePaymentIntentId,
    ...content,
  });
}
